import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Bell, ChevronRight, MessageCircle, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { ROUTES } from "@/lib/constants";
import { useAuthStore } from "@/stores/useAuthStore";
import { useAttentionItems } from "@/hooks/useAttentionItems";
import { useUnreadCounts } from "@/hooks/useUnreadCounts";
import { useNotifications } from "@/hooks/useNotifications";
import { useContentLocalizer } from "@/hooks/useContentLocalizer";
import { useDraggable, type DraggablePosition } from "@/hooks/useDraggable";
import { isActionable, linkFor, sortItems } from "./attentionModel";
import { attentionLine } from "./attentionCopy";
import type { AttentionItem } from "./types";

/** Rows the panel lists before handing off to the dashboard's full list. */
const MAX_ROWS = 5;
const PANEL_WIDTH_PX = 320;
const GUTTER_PX = 16;
/**
 * Pages the bubble stays off: Notifications already is the list, and on the
 * /get-ready and onboarding gates every link would bounce straight back to the
 * gate (where it also covered the 18+ / Terms line at phone width).
 */
const HIDDEN_ON: readonly string[] = [
  ROUTES.NOTIFICATIONS,
  ROUTES.GET_READY,
  ROUTES.ONBOARDING,
  ROUTES.ORG_ONBOARDING,
];

const SEVERITY_DOT: Record<AttentionItem["severity"], string> = {
  urgent: "bg-error-500",
  action: "bg-warning-500",
  waiting: "bg-surface-tertiary",
  info: "bg-surface-tertiary",
};

/**
 * What the bubble counts: only things waiting on the reader. "Waiting" and
 * "info" attention rows stay on the dashboard — a bubble that pulses for
 * "not on you" would teach people to ignore it.
 */
export function bubbleSummary(input: {
  items: AttentionItem[];
  unreadChats: number;
  unreadNotifications: number;
}) {
  const todo = sortItems(input.items.filter(isActionable));
  return {
    todo,
    total: todo.length + input.unreadChats + input.unreadNotifications,
  };
}

/** Opens the panel on whichever side of the bubble has room. */
function panelStyle(anchor: DOMRect): React.CSSProperties {
  const width = Math.min(PANEL_WIDTH_PX, window.innerWidth - GUTTER_PX * 2);
  const centred = anchor.left + anchor.width / 2 - width / 2;
  const left = Math.min(Math.max(centred, GUTTER_PX), window.innerWidth - width - GUTTER_PX);
  const below = anchor.top < window.innerHeight / 2;
  return below
    ? { left, width, top: anchor.bottom + 8 }
    : { left, width, bottom: window.innerHeight - anchor.top + 8 };
}

function draggedStyle(position: DraggablePosition | null): React.CSSProperties | undefined {
  if (!position) return undefined;
  return { left: position.x, top: position.y, right: "auto", bottom: "auto" };
}

/**
 * A floating, draggable bubble that pulses while something needs the reader:
 * actionable attention rows, unread chats and unread notifications. Tapping it
 * opens a short list; it hides itself once there is nothing left to do.
 */
export function AttentionBubble() {
  const { t } = useTranslation();
  const localize = useContentLocalizer();
  const { pathname } = useLocation();
  const signedIn = useAuthStore((s) => !!s.session?.userId);
  const { items } = useAttentionItems();
  const { unreadChats } = useUnreadCounts();
  const { unreadCount: unreadNotifications } = useNotifications();
  const drag = useDraggable<HTMLButtonElement>();
  const panelRef = useRef<HTMLDivElement>(null);
  const [anchor, setAnchor] = useState<DOMRect | null>(null);
  const open = anchor !== null;

  const { todo, total } = bubbleSummary({ items, unreadChats, unreadNotifications });

  // Following a row navigates — close behind it.
  useEffect(() => setAnchor(null), [pathname]);

  // The panel opened against the bubble's old spot; a drag invalidates it.
  useEffect(() => {
    if (drag.dragging) setAnchor(null);
  }, [drag.dragging]);

  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setAnchor(null);
    }
    function onPointer(e: PointerEvent) {
      const target = e.target as Node;
      if (panelRef.current?.contains(target) || drag.ref.current?.contains(target)) return;
      setAnchor(null);
    }
    window.addEventListener("keydown", onKey);
    window.addEventListener("pointerdown", onPointer);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("pointerdown", onPointer);
    };
  }, [open, drag.ref]);

  // The notifications page already is this list.
  if (!signedIn || total === 0 || HIDDEN_ON.includes(pathname)) return null;

  const label = t("attention.bubbleLabel", { count: total });

  return (
    <>
      <button
        ref={drag.ref}
        {...drag.handleProps}
        type="button"
        onClick={() => setAnchor(open ? null : (drag.ref.current?.getBoundingClientRect() ?? null))}
        style={draggedStyle(drag.position)}
        aria-label={label}
        aria-expanded={open}
        title={label}
        data-testid="attention-bubble"
        className={cn(
          "fixed z-[60] flex h-12 w-12 items-center justify-center rounded-full touch-none",
          // Bottom-left so it never stacks on the debug badge (bottom-right),
          // and above the 5rem BottomNav below md.
          !drag.position && "bottom-[calc(5.5rem+env(safe-area-inset-bottom))] left-4 md:bottom-5 md:left-5",
          "bg-warning-500 text-white shadow-lg shadow-black/25",
          drag.dragging ? "cursor-grabbing" : "cursor-grab active:scale-90 transition-transform duration-150",
        )}
      >
        {/* The pulse: a ring that keeps expanding out while anything waits. */}
        {!open && (
          <span
            aria-hidden="true"
            className="absolute inset-0 rounded-full bg-warning-500 opacity-60 motion-safe:animate-ping"
          />
        )}
        <Bell className="relative h-5 w-5" />
        <span
          aria-hidden="true"
          className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full bg-error-500 px-1 text-[11px] font-bold leading-none text-white ring-2 ring-surface"
        >
          {total > 99 ? "99+" : total}
        </span>
      </button>

      {anchor && (
        <div
          ref={panelRef}
          role="dialog"
          aria-label={t("attention.title")}
          style={panelStyle(anchor)}
          className="fixed z-[60] overflow-hidden rounded-xl border border-border-default bg-surface shadow-xl"
        >
          <div className="flex items-center justify-between border-b border-border-default px-4 py-2.5">
            <h2 className="text-sm font-semibold text-text-primary">{t("attention.title")}</h2>
            <button
              type="button"
              onClick={() => setAnchor(null)}
              aria-label={t("common.close")}
              className="rounded p-1 text-text-tertiary hover:bg-surface-secondary"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          <div className="max-h-[50vh] overflow-y-auto p-2">
            {todo.slice(0, MAX_ROWS).map((item, index) => (
              <Link
                key={`${item.kind}:${item.questId}:${item.taskId ?? ""}:${index}`}
                to={linkFor(item)}
                className="flex min-w-0 items-center gap-3 rounded-lg px-2 py-2 hover:bg-surface-secondary"
              >
                <span className={cn("h-2 w-2 shrink-0 rounded-full", SEVERITY_DOT[item.severity])} />
                <span className="min-w-0 flex-1 truncate text-sm text-text-primary">
                  {attentionLine(item, t, localize)}
                </span>
                <ChevronRight className="h-4 w-4 shrink-0 text-text-tertiary" />
              </Link>
            ))}
            {todo.length > MAX_ROWS && (
              <Link
                to={ROUTES.DASHBOARD}
                className="block rounded-lg px-2 py-2 text-sm font-medium text-primary-600 hover:bg-surface-secondary"
              >
                {t("attention.seeAll", { count: todo.length })}
              </Link>
            )}
            {unreadChats > 0 && (
              <Link
                to={ROUTES.CHATS}
                className="flex min-w-0 items-center gap-3 rounded-lg px-2 py-2 hover:bg-surface-secondary"
              >
                <MessageCircle className="h-4 w-4 shrink-0 text-primary-600" />
                <span className="min-w-0 flex-1 truncate text-sm text-text-primary">
                  {t("attention.unreadChats", { count: unreadChats })}
                </span>
                <ChevronRight className="h-4 w-4 shrink-0 text-text-tertiary" />
              </Link>
            )}
            {unreadNotifications > 0 && (
              <Link
                to={ROUTES.NOTIFICATIONS}
                className="flex min-w-0 items-center gap-3 rounded-lg px-2 py-2 hover:bg-surface-secondary"
              >
                <Bell className="h-4 w-4 shrink-0 text-primary-600" />
                <span className="min-w-0 flex-1 truncate text-sm text-text-primary">
                  {t("attention.unreadNotifications", { count: unreadNotifications })}
                </span>
                <ChevronRight className="h-4 w-4 shrink-0 text-text-tertiary" />
              </Link>
            )}
          </div>
        </div>
      )}
    </>
  );
}
